/**
 * کشف — قرینه‌ی apps/mobile/app/(tabs)/discover.tsx.
 *
 * جستجو، فیلترِ دسته، فیلترِ زمان و ترتیب، همه از `packages/core` می‌آن
 * (`discoverSearch` / `discoverWhen` / `discoverSort`). یعنی «فردا عصر، رایگان،
 * نزدیک‌ترین» روی گوشی و سایت **دقیقاً همون فهرست** رو می‌ده، نه دو تا
 * پیاده‌سازیِ شبیهِ هم.
 *
 * دو چیزِ موبایل این‌جا نیست، و هر دو دلیلِ روشن دارن:
 *  - **نقشه**: یه کتابخانه‌ی نقشه‌ی وب می‌خواد که هنوز تصمیمش گرفته نشده.
 *  - **نماهای مجله‌ای/عکسی**: روی گوشی برای پر کردنِ عرضِ باریکن؛ روی مانیتور
 *    یه گریدِ واحد کارِ هر سه رو می‌کنه.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  businessRepo, discoverIndexRepo, eventRepo, settingsRepo, tagFollowRepo, tagRepo,
} from '@/core/data/miscRepo';
import { popularityRepo } from '@/core/data/popularityRepo';
import { useSession } from '@/core/session';
import { useCityScope } from '@/core/lib/cityScope';
import { getLocale, t, useLocale } from '@/core/lib/i18n';
import { tagText } from '@/core/lib/tagLabel';
import { eventDateLabel } from '@/core/lib/dates';
import { SafeImg } from '@/ui/SafeImg';
import { cardAddress } from '@/core/lib/address';
import { matchesTokens, queryTokens, searchTextOf } from '@/core/lib/discoverSearch';
import { EMPTY_WHEN, WHEN_KEY, WHEN_PRESETS, hasWhen, matchesWhen } from '@/core/lib/discoverWhen';
import {
  SORT_KEY, sortItems, sortsFor, type Sortable,
} from '@/core/lib/discoverSort';
import { TRACE_OWNER } from '@/core/lib/trace';
import { favoriteKey, favoritesFirst, useFavoriteSnapshot } from '@/core/lib/favoriteOrder';
import { reportError } from '@/core/lib/report';
import { tagIdsOf, type Business, type EventItem, type Tag } from '@/core/domain/types';
import {
  ALWAYS_MORE_SLUGS, applyHierarchy, demoteLast, tallyTags, topTags, withKnownTags,
  type Taggable,
} from '@/core/lib/tagCounts';
import { fitChipRows } from '@/core/lib/chipFit';
import type { DiscoverSort } from '@/core/lib/prefs';
import { Card, Note } from '@/ui/kit';
import { PageBanner } from '@/ui/PageBanner';
import { NudgeBar } from '@/features/signup/SignupGate';
import {
  BellIcon, CategoryIcon, CloseIcon, FilterIcon, MoreIcon, SortIcon,
} from '@/ui/icons';
import { useIsNarrow } from '@/ui/useIsNarrow';
import { useOutsideClose } from '@/ui/useOutsideClose';
import { CityMenu } from './CityMenu';
import { FavButton, useFavorites, type Favorites } from './favorites';
import { NewPlanButton } from './newPlan';
import { IssueButton, useIssues, type Issues } from './issues';
import { useMyLocation } from './useMyLocation';
import { PushOfferCard } from '@/features/notifications/PushOfferCard';
import { MyEventsStrip, useMyPins } from './MyEventsStrip';
import { useImpressions, type Impressions } from './useImpressions';
import { readFilters, writeFilters } from './keepFilters';
import { PioneerCard } from './PioneerCard';
import { emptyKind } from '@/core/lib/pioneerCity';

type Row =
  | { kind: 'event'; item: EventItem }
  | { kind: 'place'; item: Business };

export function DiscoverPage() {
  const locale = useLocale();
  const city = useCityScope();
  /**
   * همون ۸۶۰ی کلِ سایت (ui/useIsNarrow): این‌جا فقط تصمیم می‌گیره چیپ‌های
   * دسته کجا رندر بشن. عمداً CSS نیست — با `display:none` هر دو نسخه ساخته
   * می‌شدن و یه چیپ دو تا دکمه‌ی هم‌اسم توی درختِ دسترس‌پذیری می‌شد.
   */
  const narrow = useIsNarrow();
  /**
   * حساب، همین بالا: هم `useImpressions` پایین لازمش داره، هم فهرستِ ترتیب‌ها
   * که باید قبل از اولین محاسبه‌ی فهرست بدونه صاحبِ پروژه پشتِ صفحه‌ست یا نه
   * (همون حسابی که لاگ و فهرستِ مشکل‌ها هم به اسمشه — core/lib/trace).
   */
  const { session, profile } = useSession();
  const isOwner = profile?.username === TRACE_OWNER;
  const isAdmin = !!profile?.isAdmin;

  /**
   * زنگوله‌ی دنبال کردنِ تگ (مایگریشن 0094) — با انتخابِ یه دسته، کنارِ
   * چیپش توی نوارِ جستجو ظاهر می‌شه. مهمان اصلاً نمی‌بیندش.
   */
  const qcFollows = useQueryClient();
  const tagFollows = useQuery({
    queryKey: ['tagFollows'],
    queryFn: tagFollowRepo.list,
    enabled: !!profile,
  });
  const followTag = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) =>
      tagFollowRepo.set(id, on, profile!.id),
    onSuccess: () => qcFollows.invalidateQueries({ queryKey: ['tagFollows'] }),
    onError: (e) => reportError('web.discover.followTag', e),
  });
  const followedIds = tagFollows.data ?? [];
  /**
   * حالتِ اولیه‌ی نوار از همین نشستِ مرورگر می‌آد، نه از خالی (WHOSUP-232):
   * باز کردنِ یه رخداد این صفحه رو از درخت بیرون می‌بره و برگشت یه mountِ
   * تازه‌ست، پس بدونِ این، جستجو و دسته و فیلترِ زمان هر بار می‌پرن — برخلافِ
   * گوشی که تبش سرِ جاش می‌مونه. نگاه کن به keepFilters.
   */
  const [saved] = useState(readFilters);
  const [q, setQ] = useState(saved.q);
  const [tagId, setTagId] = useState<number | null>(saved.tagId);
  const [when, setWhen] = useState(saved.when);
  const [sort, setSort] = useState<DiscoverSort>(saved.sort);
  const [kinds, setKinds] = useState<{ events: boolean; places: boolean }>(saved.kinds);
  /** «بیشتر» — هشت دسته‌ی اول، یا همه‌شون. نگاه کن به `cats` پایین. */
  const [allCats, setAllCats] = useState(saved.allCats);
  useEffect(() => {
    writeFilters({ q, tagId, when, sort, kinds, allCats });
  }, [q, tagId, when, sort, kinds, allCats]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const filterWrap = useRef<HTMLDivElement>(null);
  const sortWrap = useRef<HTMLDivElement>(null);
  useOutsideClose(filterWrap, filterOpen, () => setFilterOpen(false));
  useOutsideClose(sortWrap, sortOpen, () => setSortOpen(false));

  // همون یه فهرستِ تگِ همه‌جا — دیگه «فهرستِ کشف» جدایی وجود نداره.
  const tags = useQuery({ queryKey: ['tags'], queryFn: tagRepo.list });
  /**
   * دسته‌ی انتخاب‌شده، بیرونِ کادرِ فیلتر هم دیده می‌شه: چیپی چسبیده به نوارِ
   * جستجو، با ضربدرِ حذف و زنگوله‌ی دنبال کردن (بازخورد ۲۰۲۶-۰۸-۲۱). خودِ
   * چیپش داخلِ کادر هم روشن می‌مونه — این‌جا فقط برای اینه که با بسته بودنِ
   * کادر هم معلوم باشه فهرست روی چی تنگ شده.
   */
  const selectedTag = (tags.data ?? []).find((x) => x.id === tagId) ?? null;
  /**
   * دسته‌ی ذخیره‌شده‌ای که دیگه وجود نداره (کنار گذاشته شده یا ادغام شده —
   * مایگریشن 0086) برداشته می‌شه. وگرنه فهرست خالی می‌موند بدونِ اینکه چیپی
   * بگه چرا، و راهی هم برای برداشتنش نبود.
   */
  useEffect(() => {
    if (tagId !== null && tags.data && !selectedTag) setTagId(null);
  }, [tagId, tags.data, selectedTag]);
  // ترتیبِ چیدنِ دسته‌ها؛ ادمین از پنل عوضش می‌کند (مایگریشن 0088).
  const mergePick = useQuery({
    queryKey: ['setting', 'tagMergePick'], queryFn: settingsRepo.tagMergePick,
  });
  const cityId = city?.id ?? null;

  /**
   * فهرستِ سبکِ کلِ شهر — فقط برای عددِ کنارِ دسته‌ها.
   *
   * بدونِ این، شمارش روی چیزی بود که **بار شده**: تا وقتی کاربر تا ته اسکرول
   * نکرده بود «موسیقی ۱۷» می‌نشست بالای شهری که ۱۹۰ رخدادِ موسیقی داشت، و
   * هشت کاشیِ اول هم عوضی انتخاب می‌شدن. حالا عدد از اول درسته و با اسکرول
   * تکون نمی‌خوره — نگاه کن به DiscoverIndexItem.
   */
  const tagIndex = useQuery({
    queryKey: ['discoverIndex', cityId],
    queryFn: () => discoverIndexRepo.list(cityId!),
    enabled: cityId !== null,
    staleTime: 5 * 60_000,
  });

  /**
   * وب مثل موبایل برای مرتب‌سازی به کل فهرستِ شهر نیاز دارد. گرفتنِ صفحه‌های
   * بعدی با اسکرول باعث می‌شد نتیجه‌ی اولیه با ترتیبِ سرور دیده شود و تازه بعد
   * از پایین رفتن و برگشتن مرتب شود. repository خودش فهرست کامل را می‌گیرد و
   * اگر به سقف هزار ردیفیِ PostgREST برسد هشدار ثبت می‌کند.
   */
  const events = useQuery({
    queryKey: ['events', cityId],
    queryFn: () => eventRepo.upcoming(cityId!),
    enabled: cityId !== null,
  });
  const places = useQuery({
    queryKey: ['places', cityId],
    queryFn: () => businessRepo.list(cityId!),
    enabled: cityId !== null,
  });
  const eventRows = events.data ?? [];
  const placeRows = places.data ?? [];

  /**
   * امتیازِ محبوبیت و زمانِ افزودن — دو عددی که فقط سرور می‌دونه.
   *
   * بدونِ این، انتخابِ «محبوب‌ترین» هیچ اثری نداشت: `sortItems` با نقشه‌ی
   * خالی برای همه صفر برمی‌گردوند و ترتیب دست‌نخورده می‌موند.
   *
   * `staleTime` چند دقیقه‌ست چون این عدد لحظه‌ای نیست — با هر نشان و هر بازدید
   * یه‌ذره تکون می‌خوره و کسی منتظرِ دیدنِ اثرِ فوریش نیست. بدونِ حساب هم
   * خونده می‌شه: `event_popularity` فقط عددِ سرجمعه، نه اینکه «چه کسی»
   * (مایگریشن 0049)، و مهمان هم صفحه‌ی کشف رو داره.
   *
   * بدونِ شهر توی کلید: کوئری فیلترِ شهر نداره، پس یه فهرست برای همه‌ی
   * شهرهاست و عوض کردنِ شهر نباید دوباره بگیردش.
   */
  const metas = useQuery({
    queryKey: ['itemMetas'],
    queryFn: popularityRepo.metas,
    staleTime: 5 * 60_000,
  });
  const metaMap = useMemo(
    () => new Map((metas.data ?? []).map((m) => [m.id, m])), [metas.data],
  );

  /**
   * زمانِ نشانِ ادمین (`flagged_at`) — سوختِ ترتیبِ «نشان‌دارها اول».
   *
   * نگاشتِ سبکِ جدا و نه ستونی روی خودِ ردیف، به همون دلیلی که موبایل داره
   * (نگاه کن به adminRepo.discoverMarks): نشان چیزیه که فقط ادمین می‌بیندش،
   * و فهرستِ کشف رو مهمان هم می‌گیره.
   *
   * `enabled` یعنی برای بقیه اصلاً درخواستی نمی‌ره — نه فقط گزینه‌ش رو
   * نمی‌بینن، داده‌ش هم خواسته نمی‌شه.
   *
   * از WHOSUP-209 به بعد همون تابعی صدا زده می‌شه که موبایل صدا می‌زنه و
   * چهار نشانه رو با هم می‌ده. وب فعلاً فقط `flagged` رو به کار می‌بره و سه
   * تای دیگه هدر می‌ره؛ عمدیه و ارزون‌تر از دو مسیرِ جدا برای یک سوال — و
   * مهم‌تر، این یکی **شهری** و بی‌سقف است، برخلافِ چهارتاییِ قبلی که کلِ
   * دیتابیس رو می‌خوندن.
   *
   * `import()` وسطِ تابع و نه بالای فایل، عمدی: قرارِ این اپ اینه که کدِ
   * ادمین توی باندلش نباشه (ابزارِ بازبینی برای همین رفت apps/admin). یه
   * `import` معمولی کلِ `adminRepo` رو می‌کشید توی چانکِ اصلی و همه — از
   * جمله مهمون — دانلودش می‌کردن، برای تابعی که فقط ادمین صداش می‌زنه.
   * این‌جوری ویت چانکِ جدا می‌سازه و فقط همون‌جا که `enabled` باز می‌شه
   * گرفته می‌شه.
   */
  const marks = useQuery({
    queryKey: ['adminDiscoverMarks', cityId],
    queryFn: () => import('@/core/data/adminRepo')
      .then((m) => m.adminRepo.discoverMarks(cityId!)),
    enabled: isAdmin && cityId !== null,
    staleTime: 60_000,
  });
  const flagMap = useMemo(
    () => new Map(Object.entries(marks.data?.flagged ?? {})), [marks.data],
  );

  const myLocation = useMyLocation();
  const sortCtx = useMemo(
    // زبان فقط به کارِ ترتیبِ الفبایی می‌آد: «ب» باید قبلِ «پ» بیاد، و این
    // چیزیه که فقط جدولِ همون زبان می‌دونه.
    () => ({
      metas: metaMap, origin: myLocation.coords, flags: flagMap, locale: getLocale(),
    }),
    [metaMap, myLocation.coords, flagMap, locale],
  );

  /**
   * گزینه‌های ترتیبِ این حساب.
   *
   * از ترتیب‌های ادمین فقط «نشان‌دارها اول» این‌جاست: «اولویتِ بازبینی» عددش
   * از `metadata` می‌آد و سیم‌کشیِ خودش رو می‌خواد که وب هنوز نداره، و
   * گزینه‌ای که هیچ‌کاری نمی‌کنه بدتر از نبودنشه. برداشتنش این‌جاست و نه توی
   * `sortsFor`، چون این کمبودِ همین صفحه‌ست نه قاعده‌ی مشترکِ دو اپ.
   */
  const sortOptions = useMemo<DiscoverSort[]>(
    () => sortsFor({ owner: isOwner, admin: isAdmin })
      .filter((s2) => s2 !== 'reviewPriority'),
    [isOwner, isAdmin],
  );

  /**
   * ترتیبی که واقعاً اجرا می‌شه — قرینه‌ی `activeSort`ِ موبایل.
   *
   * ترتیبِ الفبایی فقط مالِ صاحبِ پروژه‌ست و نشان‌دارها فقط مالِ ادمین، و
   * گزینه‌شون هم فقط برای همون‌ها رندر می‌شه؛ ولی خروج از حساب وسطِ همون صفحه
   * انتخابِ قبلی رو پاک نمی‌کنه، و فهرستی که به یه گزینه‌ی نادیدنی چیده شده
   * باشه فقط خراب به‌نظر می‌آد.
   *
   * شرطش «توی فهرستِ گزینه‌ها هست یا نه» است و نه یه فهرستِ دستیِ دوم: هر
   * ترتیبی که این صفحه سوختش رو نداره، همون‌جا از فهرست افتاده.
   */
  const activeSort: DiscoverSort =
    sortOptions.includes(sort) ? sort : 'nextTime';

  const changeSort = (next: DiscoverSort) => {
    setSort(next);
    setSortOpen(false);
    // «قبل از مرتب کردن، اگه اینترنت هست عددها تازه شن» — همون کاری که موبایل
    // موقعِ عوض شدنِ ترتیب می‌کنه. شکستش بی‌صداست و عددهای قبلی می‌مونن.
    if (next === 'popularity') metas.refetch();
    // اجازه‌ی موقعیت دقیقاً همین‌جا پرسیده می‌شه، نه موقعِ باز شدنِ صفحه: تا
    // کسی فاصله نخواسته، دلیلی برای پرسیدن نیست.
    if (next === 'distance') myLocation.ensure();
    // نشان رو خودِ ادمین چند لحظه پیش گذاشته و انتظار داره همین‌جا اثرش رو
    // ببینه، پس لحظه‌ی انتخاب تازه‌ترین چیزیه که می‌شه بهش رسید.
    if (next === 'flagged') marks.refetch();
  };

  /**
   * چرا ترتیبِ انتخاب‌شده جواب نداده — قرینه‌ی `sortNote`ِ موبایل.
   *
   * بدونِ این، انتخابِ «نزدیک‌ترین» وقتی اجازه رد شده باشه یعنی هیچ اتفاقی
   * نمی‌افته و کاربر نمی‌فهمه چرا.
   */
  const sortNote = sort === 'distance' && !myLocation.coords
    ? (myLocation.state === 'denied' ? t('sortDistanceDenied')
      : myLocation.state === 'failed' ? t('sortDistanceFailed') : null)
    // همون منطق برای «نشان‌دارها اول»: چیزی نشان نخورده (یا خطای شبکه) یعنی
    // فهرست تکون نمی‌خوره — و فهرستی که تکون نمی‌خوره بدونِ توضیح، شبیهِ
    // ترتیبِ خرابه.
    : activeSort === 'flagged' && !marks.isLoading && flagMap.size === 0
      ? t('sortFlaggedEmpty') : null;

  /**
   * نشان‌شده‌ها بالای فهرست — همون قاعده‌ی گوشی.
   *
   * ترتیب از یه عکسِ لحظه‌ای می‌خونه، نه از مجموعه‌ی زنده (نگاه کن به
   * core/lib/favoriteOrder)، پس زدنِ قلب کارت رو زیرِ دستِ کاربر جابه‌جا
   * نمی‌کنه. این‌جا نشانه‌ی رفرش لازم نیست: صفحه با رفتن به یه مسیرِ دیگه از
   * درخت بیرون می‌ره، پس «ورودِ دوباره» خودش یه mountِ تازه‌ست.
   */
  const fav = useFavorites();
  const favSnapshot = useFavoriteSnapshot(fav.keys, fav.settled);

  /**
   * «مشکلِ این رخداد» — فقط برای صاحبِ پروژه، و مثلِ نشان‌شده‌ها یک بار برای
   * کلِ صفحه گرفته می‌شود، نه یکی به‌ازای هر کارت (features/discover/issues).
   */
  const issues = useIssues();

  /**
   * شمردنِ «کارتش دیده شد» — فقط برای کاربرِ واردشده (نگاه کن به
   * useImpressions). این عدد فقط یه جا خونده می‌شه: نوارِ پینِ خودِ صاحبِ
   * رخداد، به‌عنوانِ مخرجِ «چند نفر بازش کردن».
   */
  const imp = useImpressions(!!session);

  /** رخدادهای پین‌شده‌ی خودم — از ردیف‌های فهرست بیرون کشیده می‌شوند. */
  const myPins = useMyPins(cityId);

  /**
   * فهرست **قبل از** فیلترِ دسته — پایه‌ی هر دو چیزی که به دسته‌ها مربوطه:
   * عددِ کنارِ هر آیکون، و اینکه اصلاً کدوم آیکون‌ها بیان.
   *
   * چرا قبل از خودش: عددِ کنارِ «موسیقی» باید بگه «اگه این رو بزنم چندتا
   * می‌مونه»، نه اینکه با انتخابِ «ورزش» صفر شه — همون قاعده‌ای که چیپ‌های
   * گوشی از قبل داشتن.
   */
  const pool = useMemo<Row[]>(() => {
    const tokens = queryTokens(q);
    const all: Row[] = [
      // رخدادهای پین‌شده‌ی خودم از ردیف‌ها بیرون می‌مانند: کارتشان بالای همین
      // صفحه است و دو بار دیدنِ یک چیز چیزی به کسی اضافه نمی‌کند.
      ...(kinds.events
        ? eventRows.filter((e) => !myPins.ids.has(e.id))
          .map((item) => ({ kind: 'event' as const, item }))
        : []),
      ...(kinds.places ? placeRows.map((item) => ({ kind: 'place' as const, item })) : []),
    ];

    // «الان» یک‌بار در هر محاسبه گرفته می‌شه، نه داخلِ هر مقایسه — وگرنه
    // «امروز» می‌تونه وسطِ فیلتر کردنِ یه فهرست به «فردا» بپره.
    const now = new Date();

    return all.filter((row) => {
      if (tokens.length && !matchesTokens(searchTextOf(row.item), tokens)) return false;
      // فیلترِ زمان فقط به رخدادها می‌خوره — همون قاعده‌ی موبایل: مکان توی
      // داده‌ش نه تاریخ داره نه ساعتِ کار، پس «امروز» چیزی درباره‌ش نمی‌دونه
      // که بخواد حذفش کنه.
      if (row.kind === 'event' && !matchesWhen(row.item, when, now)) return false;
      return true;
    });
  }, [eventRows, placeRows, q, when, kinds, myPins.ids]);

  const rows = useMemo<Row[]>(() => {
    const kept = tagId === null ? pool
      : pool.filter((row) => tagIdsOf(row.item).includes(tagId));

    // مرتب‌سازی روی خودِ آیتم‌ها انجام می‌شه و بعد دوباره به سطر برمی‌گرده —
    // `sortItems` با `Sortable` کار می‌کنه و از رخداد/مکان بودن خبر نداره.
    const sorted = sortItems(kept.map((r) => r.item as Sortable), activeSort, sortCtx);
    const byId = new Map(kept.map((r) => [r.item.id, r]));
    const rowsSorted = sorted.map((x) => byId.get(x.id)).filter((r): r is Row => !!r);
    // جدولِ favorites نوعِ مکان رو `business` می‌شناسه؛ سطرِ این صفحه اسمش
    // `place` است. همین یه نگاشت، وگرنه هیچ مکانی نشان‌شده حساب نمی‌شد.
    return favoritesFirst(rowsSorted, favSnapshot,
      (r) => favoriteKey(r.kind === 'event' ? 'event' : 'business', r.item.id));
  }, [pool, tagId, activeSort, favSnapshot, sortCtx]);

  /**
   * دسته‌های صفحه: چند تا آیتم دارن، و کدوم‌هاشون روی صفحه‌ی اول می‌شینن.
   *
   * قاعده‌ی چیدن توی `core/lib/tagCounts` است و این‌جا فقط به فهرستِ تگ‌ها
   * وصل می‌شه. تگی که توی فهرست نیست — کنار گذاشته شده (مایگریشن 0086) یا
   * روی یه رخدادِ کش‌شده مونده — با `withKnownTags` **قبلِ** شمارش می‌افته
   * بیرون، و آیتمی که هیچ تگی براش نمی‌مونه می‌ره زیرِ «سایر».
   */
  const tally = useMemo(() => {
    /**
     * جستجوی متنی توی فهرستِ سبک نیست (متن همون چیزیه که سنگینش می‌کنه)، پس
     * تا وقتی چیزی تایپ شده شمارش برمی‌گرده روی ردیف‌های بارشده — یعنی
     * دقیقاً همون‌هایی که فهرست هم نشون می‌ده. بدونِ این، عددها کلِ شهر رو
     * می‌گفتن در حالی که زیرشون سه تا کارت بود.
     */
    // تگ‌های زنده و پناهگاهشون؛ هر دو مسیرِ شمارش از همین رد می‌شن.
    const known = new Set((tags.data ?? []).map((x) => x.id));
    const other = (tags.data ?? []).find((x) => x.slug === 'other')?.id ?? null;
    const pick = mergePick.data ?? 'most';
    const tally_ = (list: readonly Taggable[]) =>
      tallyTags(withKnownTags(list, known, other), { pick });

    if (!tagIndex.data || queryTokens(q).length > 0) return tally_(rows.map((r) => r.item));
    const now = new Date();
    return tally_(tagIndex.data.filter((x) => {
      if (x.kind === 'event' ? !kinds.events : !kinds.places) return false;
      // رخدادهای پین‌شده‌ی خودم توی ردیف‌ها نیستن، پس توی شمارش هم نباشن.
      if (myPins.ids.has(x.id)) return false;
      if (x.kind === 'event' && !matchesWhen(x, when, now)) return false;
      if (tagId !== null && !tagIdsOf(x).includes(tagId)) return false;
      return true;
    }));
  }, [tagIndex.data, rows, q, kinds, when, tagId, myPins.ids, tags.data, mergePick.data]);
  const cats = useMemo(() => {
    const byId = new Map((tags.data ?? []).map((x) => [x.id, x]));
    const selected = tagId === null ? [] : [tagId];
    /**
     * سلسله‌مراتبِ دستیِ ادمین (مایگریشن 0084): تا وقتی مادر روی ردیفه،
     * فرزندهاش نمی‌آن — نه این‌جا نه پشتِ «بیشتر». با فیلتر کردن روی خودِ
     * مادر باز می‌شن.
     */
    const parentOf = new Map((tags.data ?? []).map((x) => [x.id, x.parentId]));
    const visible = applyHierarchy(tally, parentOf, selected);
    const withTag = (list: typeof tally) => list
      .map((x) => ({ ...x, tag: byId.get(x.id) }))
      .filter((x): x is typeof x & { tag: Tag } => !!x.tag);
    /**
     * «سایر» هم پشتِ «بیشتر» می‌ماند و هم آخرین چیپِ فهرست است — حتی وقتی
     * پرتکرارترینِ شهر باشد، که معمولاً هست. `demoteLast` قبل از `topTags`
     * می‌آید تا اگر خودش انتخاب شده و به همین دلیل ماندگار شده، باز هم ته
     * صف باشد. نگاه کن به ALWAYS_MORE_SLUGS.
     */
    const demote = (tags.data ?? [])
      .filter((x) => ALWAYS_MORE_SLUGS.includes(x.slug)).map((x) => x.id);
    const ordered = demoteLast(visible, demote);
    const head = withTag(topTags(ordered, { selected, demote }));
    const full = withTag(ordered);
    return { head, full, shown: allCats ? full : head };
  }, [tally, tags.data, allCats, tagId]);

  /**
   * سربرگِ چسبان با «برگشتنِ اسکرول» (WHOSUP-157): نوارِ جستجو و دسته‌ها با
   * اولین اسکرول به بالا برمی‌گردند، بی‌آنکه لازم باشد تا سرِ صفحه بروی؛
   * پایین رفتن دوباره می‌بردشان. بنر عمداً توی این سربرگ نیست — در جریانِ
   * عادیِ صفحه است و فقط همان بالا دیده می‌شود، که خواستِ صریحِ کاربر بود.
   *
   * آستانه‌ی ۶ پیکسل لرزشِ تاچ‌پد را می‌گیرد؛ نزدیکِ سرِ صفحه همیشه پیداست
   * تا با بنر و سربرگ هم‌زمان روی صفحه شروع کنیم، نه با یک پرش.
   */
  const [headShown, setHeadShown] = useState(true);
  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const d = y - last;
      last = y;
      if (y < 80) { setHeadShown(true); return; }
      if (d > 6) setHeadShown(false);
      else if (d < -6) setHeadShown(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const busy = cityId === null || events.isLoading || places.isLoading;
  /**
   * نقطه‌ی روی دکمه‌ی فیلتر: «چیزی این تو روشنه». دسته هم حالا همین توست،
   * پس اون هم می‌شمره — وگرنه فهرستِ فیلترشده بود و دکمه‌ای که می‌گفت هیچی
   * انتخاب نشده.
   */
  const filterActive = !kinds.events || !kinds.places || hasWhen(when) || tagId !== null;

  return (
    <div className="feed discover">
      {/* بنرِ معرفی — مثل برنامه‌ها و دوستان؛ فقط صفحه‌ی پهن، بسته‌شدنی.
          در جریانِ عادی می‌ماند و با اسکرول می‌رود؛ سربرگِ زیرش چسبان است. */}
      <PageBanner kind="discover" />
      {/* یادآوریِ ملایمِ ثبت‌نام — فقط مهمان، با سقف (WHOSUP-308). موقعیتش
          ثابت است، پس جایش در درخت مهم نیست. */}
      <NudgeBar />

      <div className={`discover-head${headShown ? '' : ' away'}`}>
      <div className="discover-toolbar">
        <h1 className="feed-title compact">{t('discover')}</h1>

        <CityMenu />

        {/* دسته‌ی انتخاب‌شده همین‌جا می‌شینه، مثل یه توکنِ جستجو: آیکون +
            اسم + زنگوله‌ی دنبال کردن (فقط واردشده‌ها) + ضربدرِ حذف. */}
        <div className="search-wrap">
          {selectedTag ? (
            <span className="search-tagchip">
              <CategoryIcon slug={selectedTag.slug} active size={17} />
              {tagText(selectedTag)}
              {profile ? (
                <button type="button"
                  className={`cat-follow tagchip-bell${followedIds.includes(selectedTag.id) ? ' on' : ''}`}
                  aria-pressed={followedIds.includes(selectedTag.id)}
                  title={`${t('followTagAction')} — ${tagText(selectedTag)}`}
                  onClick={() => followTag.mutate({
                    id: selectedTag.id, on: !followedIds.includes(selectedTag.id),
                  })}>
                  <BellIcon size={15} filled={followedIds.includes(selectedTag.id)} />
                </button>
              ) : null}
              <button type="button" className="tagpick-x"
                aria-label={`${t('remove')} — ${tagText(selectedTag)}`}
                onClick={() => setTagId(null)}>
                ×
              </button>
            </span>
          ) : null}
          {/* ضربدرِ ته کادر، مثلِ گوشی (features/discover/SearchBar): پاک
              کردنِ متن با یه کلیک، چون معمولاً دنبالِ چیزِ دیگه‌ای می‌گردن نه
              اینکه از جستجو بیرون بیان. تا چیزی تایپ نشده اصلاً نیست. */}
          <span className="search-box">
            <input className={`input${q.trim() ? ' has-query' : ''}`} placeholder={t('searchAllHint')} value={q}
              onChange={(e) => setQ(e.target.value)} />
            {q ? (
              <button type="button" className="search-x" data-tap="search-clear"
                aria-label={t('filterClear')} title={t('filterClear')}
                onClick={() => setQ('')}>
                <CloseIcon size={14} />
              </button>
            ) : null}
          </span>
        </div>

        <div className="popover-wrap" ref={filterWrap}>
          <button type="button" className={`iconbtn2${filterActive ? ' on' : ''}`}
            aria-haspopup="menu" aria-expanded={filterOpen}
            aria-label={t('filter')} title={t('filter')}
            onClick={() => setFilterOpen((v) => !v)}>
            <FilterIcon />
            {filterActive ? <span className="navdot" aria-hidden="true" /> : null}
          </button>
          {filterOpen ? (
            /* `filter-menu` فقط برای عرض است: نصفِ صفحه، هر صفحه‌ای که باشد
               (کادرِ ترتیب پایین‌تر عمداً همان عرضِ کوتاهِ خودش را دارد). */
            <div className="menu popover-menu filter-menu" role="menu">
              <p className="menu-title">{t('tabEvents')} / {t('tabPlaces')}</p>
              <div className="chiprow chips-wrap popover-row">
                <button type="button" className={`chip${kinds.events ? ' on' : ''}`}
                  data-tap="kind-events"
                  onClick={() => setKinds((k) => ({ ...k, events: !k.events }))}>
                  {t('tabEvents')}
                </button>
                <button type="button" className={`chip${kinds.places ? ' on' : ''}`}
                  data-tap="kind-places"
                  onClick={() => setKinds((k) => ({ ...k, places: !k.places }))}>
                  {t('tabPlaces')}
                </button>
              </div>
              <p className="menu-title">{t('when')}</p>
              <div className="chiprow chips-wrap popover-row">
                {WHEN_PRESETS.filter((p) => p !== 'date').map((p) => (
                  <button key={p} type="button" className={`chip${when.preset === p ? ' on' : ''}`}
                    data-tap={`when-${p}`}
                    onClick={() => setWhen((w) => (
                      w.preset === p ? EMPTY_WHEN : { ...w, preset: p, date: null }))}>
                    {t(WHEN_KEY[p])}
                  </button>
                ))}
              </div>

              {/* دسته‌ها **فقط روی صفحه‌ی باریک** این تو هستن — مثلِ اپ، که
                  همه‌ی چیزهای تنگ‌کننده‌ی فهرست توی یه کادرِ شناورن. روی
                  مانیتور جاشون ردیفِ زیرِ نوارِ جستجوست (پایین‌تر، بعد از
                  بسته شدنِ `discover-toolbar`). قاعده‌ی خودِ چیپ‌ها یکیه —
                  نگاه کن به CategoryChips. */}
              {narrow && cats.shown.length ? (
                <>
                  <p className="menu-title">{t('filterCategory')}</p>
                  <CategoryChips cats={cats} tagId={tagId} allCats={allCats}
                    onPick={setTagId} onToggleAll={() => setAllCats((v) => !v)}
                    className="popover-row" />
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="popover-wrap" ref={sortWrap}>
          <button type="button" className="iconbtn2" aria-haspopup="menu" aria-expanded={sortOpen}
            aria-label={t('sortBy')} title={t('sortBy')}
            onClick={() => setSortOpen((v) => !v)}>
            <SortIcon />
          </button>
          {sortOpen ? (
            <div className="menu popover-menu" role="menu">
              {/* همون فهرستِ موبایل: «نزدیک‌ترین» هم هست، چون موقعیت حالا
                  موقعِ انتخابِ خودش پرسیده می‌شه. از ترتیب‌های ادمین فقط
                  «نشان‌دارها اول» هست — نگاه کن به sortOptions. */}
              {sortOptions.map((s) => (
                <button key={s} type="button" role="menuitem"
                  className={`menu-item${activeSort === s ? ' active' : ''}`}
                  onClick={() => changeSort(s)}>
                  {t(SORT_KEY[s])}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {/* روی مانیتور، دسته‌ها زیرِ نوارِ جستجو بازن (خواسته‌ی ۲۰۲۶-۰۸-۲۴) و
          از WHOSUP-157 **دو سطرِ کامل** پر می‌کنن — نه یه تعدادِ ثابت — و
          «بیشتر» همیشه آخرین چیپه (حالتِ twoRow در CategoryChips). روی گوشی
          همین ردیف نصفِ صفحه‌ی اول رو می‌خورد، پس اون‌جا مثل قبل توی کادرِ
          فیلتره — بالاتر — و همون قاعده‌ی قدیمیِ «۸ تای اول» رو داره. */}
      {!narrow && cats.shown.length ? (
        <CategoryChips cats={cats} tagId={tagId} allCats={allCats}
          onPick={setTagId} onToggleAll={() => setAllCats((v) => !v)}
          className="cat-row" twoRow />
      ) : null}
      </div>{/* /discover-head */}

      {sortNote ? <Note tone="warn">{sortNote}</Note> : null}

      {/* WHOSUP-270: وقتی متنی جستجو شده، فهرست «همه» نیست — و حاشیه‌ی کادر
          بالای صفحه بعد از اسکرول دیده نمی‌شه. پس بالای فهرست صریح می‌گیم. */}
      {!busy && queryTokens(q).length ? (
        <p className="search-note">
          <span>{t('searchShowing', { q: q.trim(), n: rows.length })}</span>
          <button type="button" className="search-note-all" data-tap="search-show-all"
            onClick={() => setQ('')}>{t('searchShowAll')}</button>
        </p>
      ) : null}

      {/* اجازه‌ی اعلانِ مرورگر — تنها جای بیرونِ تنظیمات که پرسیده می‌شود.
          خودش تصمیم می‌گیرد اصلاً دیده شود یا نه (مهمان، اپ‌دار، «نه»ی
          قبلی، مرورگرِ مسدودکرده — هیچ‌کدام کارتی نمی‌بینند). */}
      <PushOfferCard />

      {/* رخدادهای خودِ کاربر — **بیرونِ** شبکه‌ی فهرست و بالای همه‌چیز.
          نه فیلتر از رویش رد می‌شود نه ترتیب، چون این‌ها مالِ خودِ اوست و
          منتظرِ کاری از او (نگاه کن به MyEventsStrip). */}
      <MyEventsStrip cityId={cityId} />

      {busy ? <p className="dim">…</p> : null}
      {/* فهرستِ خالی دو تا معنیِ کاملاً متفاوت داره: یا جستجو چیزی پیدا نکرده،
          یا این شهر اصلاً هنوز محتوایی نداره. دومی رو باید صریح گفت، وگرنه
          کاربرِ یه شهرِ تازه فکر می‌کنه اپ خرابه. */}
      {!busy && rows.length === 0 ? (
        emptyKind({
          filtering: queryTokens(q).length > 0 || tagId !== null || filterActive,
          cityHasContent: eventRows.length + placeRows.length > 0,
        }) === 'filtered' ? (
          <Card><p className="dim">{t('searchNoMatch')}</p></Card>
        ) : <PioneerCard />
      ) : null}

      <div className="grid">
        {!busy ? rows.map((row) => (
          <React.Fragment key={`${row.kind}-${row.item.id}`}>
            {row.kind === 'event'
              ? <EventCard item={row.item} fav={fav} imp={imp} issues={issues} />
              : <PlaceCard item={row.item} fav={fav} />}
          </React.Fragment>
        )) : null}
      </div>
    </div>
  );
}

/** یه دسته، همراهِ عددش و خودِ تگ — چیزی که `cats` پایینِ صفحه می‌سازه. */
interface CatChip { id: number; count: number; tag: Tag }

/**
 * چیپ‌های دسته — یه شکل، دو جا.
 *
 * روی مانیتور ردیفِ زیرِ نوارِ جستجو، روی گوشی داخلِ کادرِ فیلتر (مثلِ اپ).
 * جدا شدنش برای همینه: دو تا کپیِ این ردیف یعنی دو تا رفتار که یواش‌یواش از
 * هم دور می‌شن — همون دلیلی که FilterChips روی موبایل هم جداست.
 *
 * قاعده‌ی خودِ فهرست (چی می‌آد و به چه ترتیبی) این‌جا نیست، توی `cats` است:
 *  ۱) دسته‌ی بی‌آیتم نمی‌آد، و دسته‌ی تک‌آیتمی هم نه مگر تنها تگِ همون آیتم
 *     باشه (core/lib/tagCounts)،
 *  ۲) کنارِ هر اسم، تعدادِ آیتم‌هاش،
 *  ۳) چندتای اول و بقیه پشتِ «بیشتر»؛ «سایر» همیشه اون پشت. «چندتا» دو
 *     معنی داره: توی کادرِ فیلترِ گوشی همون قاعده‌ی قدیمیِ topTags است؛
 *     روی ردیفِ مانیتور (`twoRow`) **دو سطرِ کامل** است — هرچی جا شه،
 *     به‌علاوه‌ی «بیشتر» که همیشه آخرین چیپه (WHOSUP-157). عرض‌ها این‌جا
 *     اندازه گرفته می‌شن ولی خودِ حساب توی core/lib/chipFit است و تست داره.
 *  ۴) با انتخابِ یه دسته، خودِ فهرستِ دسته‌ها هم تنگ می‌شه: فقط اون‌هایی
 *     می‌مونن که با انتخاب‌شده همراهن و عددها همون‌جا آپدیت می‌شن — چون
 *     `tally` روی `rows` حساب می‌شه.
 */
function CategoryChips({ cats, tagId, allCats, onPick, onToggleAll, className, twoRow }: {
  cats: { head: CatChip[]; full: CatChip[]; shown: CatChip[] };
  tagId: number | null;
  allCats: boolean;
  onPick: (id: number | null) => void;
  onToggleAll: () => void;
  className: string;
  /** دو سطرِ پر به‌جای تعدادِ ثابت — فقط ردیفِ زیرِ جستجوی مانیتور. */
  twoRow?: boolean;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  /**
   * چند چیپ (بدونِ «بیشتر») توی دو سطر جا می‌شه؛ null یعنی «هنوز اندازه
   * نگرفته‌ام» — توی همون رندر همه رندر می‌شن و layout effect **قبل از
   * نقاشیِ** مرورگر عدد را می‌گذارد، پس پرشی دیده نمی‌شود.
   */
  const [fit, setFit] = useState<number | null>(null);
  const locale = getLocale();

  // متن یا خودِ فهرست که عوض شه عرض‌ها عوض می‌شن — از نو اندازه بگیر.
  useEffect(() => { if (twoRow) setFit(null); }, [twoRow, cats.full, locale]);
  useEffect(() => {
    if (!twoRow) return undefined;
    const onResize = () => setFit(null);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [twoRow]);

  useLayoutEffect(() => {
    if (!twoRow || fit !== null || allCats) return;
    const el = rowRef.current;
    if (!el || el.children.length < 2) return;
    const children = Array.from(el.children) as HTMLElement[];
    const more = children[children.length - 1]; // موقعِ اندازه‌گیری همیشه رندر شده
    const gap = parseFloat(getComputedStyle(el).columnGap) || 8;
    setFit(fitChipRows(
      children.slice(0, -1).map((c) => c.offsetWidth),
      more.offsetWidth, el.clientWidth, gap,
    ));
  }, [twoRow, fit, allCats]);

  // twoRow: تا اندازه معلوم نشده یا «بیشتر» باز است، همه؛ وگرنه به‌اندازه‌ی دو سطر.
  const shown = !twoRow ? cats.shown
    : allCats || fit === null ? cats.full : cats.full.slice(0, fit);
  const moreShown = !twoRow
    ? cats.full.length > cats.head.length
    : allCats || fit === null || fit < cats.full.length;

  return (
    <div className={`chiprow chips-wrap ${className}`} ref={rowRef}>
      {shown.map(({ id, count, tag }) => {
        const on = id === tagId;
        const label = tagText(tag);
        // ⚠ `data-tap` و نه متنِ دکمه: متنِ این چیپ نامِ تگ است (و شمارنده‌اش)،
        // و شنونده‌ی کلیکِ boot متنِ خام را ذخیره نمی‌کند. اسلاگ ثابت است،
        // بی‌زبان است و مالِ خودمان.
        return (
          <button key={id} type="button" className={`chip cat-btn${on ? ' on' : ''}`}
            aria-pressed={on} data-tap={`cat-${tag.slug}`}
            onClick={() => onPick(on ? null : id)}>
            <CategoryIcon slug={tag.slug} active={on} size={16} />
            <span className="cat-label">{label}</span>
            <span className="cat-count">{count}</span>
          </button>
        );
      })}
      {moreShown ? (
        <button type="button" className="chip cat-btn cat-more" aria-expanded={allCats}
          data-tap="cat-more" onClick={onToggleAll}>
          <MoreIcon size={16} />
          <span className="cat-label">{allCats ? t('catLess') : t('catMore')}</span>
        </button>
      ) : null}
    </div>
  );
}

function EventCard({ item, fav, imp, issues }: {
  item: EventItem; fav: Favorites; imp: Impressions; issues: Issues;
}) {
  return (
    // `has-issue` فقط جا برای پرچمِ گوشه باز می‌کند — و فقط وقتی پرچمی هست.
    <div className={`tile-wrap${issues.on ? ' has-issue' : ''}`} ref={imp.watch(item.id)}>
      <Link to={`/event/${item.id}`} className="tile">
        {/* خرابیِ عکس گزارش می‌شود — سربرگِ «عکس‌ها»ی ادمین (lib/report). */}
        <SafeImg className="tile-img" src={item.imageUrl} loading="lazy" screen="web.discover" />
        <div className="tile-body">
          <span className="tile-when">{eventDateLabel(item)}</span>
          <h3>{item.title}</h3>
          {/* رخدادی که کاربر خودش اضافه کرده venue ندارد؛ مکانش روی خودِ
              ردیف است (مایگریشن 0079). همان ترتیبِ `itemFields` در core. */}
          <p className="tile-meta">
            {item.venueName ?? cardAddress(item.address, item.city) ?? item.city ?? ''}
          </p>
          {item.isFree ? <span className="count ok">{t('free')}</span> : null}
        </div>
      </Link>
      <FavButton fav={fav} kind="event" id={item.id} className="tile-fav" />
      {/* زیرِ قلب، نه کنارش: روی کارتِ باریکِ شبکه دو دکمه‌ی چسبیده در یک
          ردیف با یک لغزشِ انگشت اشتباه زده می‌شوند. */}
      <NewPlanButton id={item.id} title={item.title} categoryId={item.categoryId}
        placeName={item.venueName ?? item.address}
        className="tile-plan" />
      {/* گوشه‌ی روبه‌رویِ قلب. برای هر حسابِ دیگری اصلاً رندر نمی‌شود. */}
      <IssueButton issues={issues} eventId={item.id} className="tile-issue"
        tagIds={item.tagIds} categoryId={item.categoryId} />
    </div>
  );
}

function PlaceCard({ item, fav }: { item: Business; fav: Favorites }) {
  return (
    <div className="tile-wrap">
      <Link to={`/place/${item.id}`} className="tile">
        <SafeImg className="tile-img" src={item.imageUrl} loading="lazy" screen="web.discover" />
        <div className="tile-body">
          <h3>{item.name}</h3>
          <p className="tile-meta">{item.address ?? item.city ?? ''}</p>
          {item.hasDiscount ? <span className="count ok">{item.discountNote ?? '%'}</span> : null}
        </div>
      </Link>
      {/* جدولِ favorites نوعِ مکان را `business` می‌شناسد، نه `place`. */}
      <FavButton fav={fav} kind="business" id={item.id} className="tile-fav" />
      <NewPlanButton id={item.id} title={item.name} categoryId={item.categoryId}
        placeName={item.address} className="tile-plan" />
    </div>
  );
}
